import React from 'react';
import { cn } from '@/lib/utils';

interface SectionProps {
  children: React.ReactNode;
  className?: string;
  id?: string;
  dark?: boolean;
}

/**
 * Reusable section wrapper component.
 * Provides consistent padding, max-width, and styling.
 *
 * @param props - Component props.
 * @param props.children - Section content.
 * @param props.className - Additional CSS classes.
 * @param props.id - Section ID for navigation.
 * @param props.dark - Whether to use dark color scheme.
 * @returns A styled section element.
 */
export const Section: React.FC<SectionProps> = ({ children, className = "", id, dark = false }) => {
  return (
    <section 
      id={id} 
      className={cn(
        "py-16 md:py-32 px-6",
        dark ? "bg-obsidian-900 text-white" : "text-obsidian",
        className
      )}
    >
      <div className="max-w-7xl mx-auto w-full">
        {children}
      </div>
    </section>
  );
};

interface SectionHeaderProps {
  label: string;
  title: string;
  children?: React.ReactNode;
  className?: string;
}

/**
 * Header component for sections with label, title, and optional content.
 *
 * @param props - Component props.
 * @param props.label - Small uppercase label text.
 * @param props.title - Main section title.
 * @param props.children - Optional additional content.
 * @param props.className - Additional CSS classes.
 * @returns A styled section header.
 */
export const SectionHeader: React.FC<SectionHeaderProps> = ({ label, title, children, className }) => (
  <div className={cn("mb-12 md:mb-24 md:flex justify-between items-end border-b border-white/5 pb-8", className)}>
    <div className="max-w-3xl">
      <span className="block text-xs font-bold uppercase tracking-widest text-premium-blue mb-4">{label}</span>
      <h2 className="text-3xl md:text-5xl font-display leading-tight text-white">{title}</h2>
    </div>
    {children && <div className="mt-6 md:mt-0 md:ml-12 max-w-sm">{children}</div>}
  </div>
);